import { useEffect, useMemo, useRef, useState } from 'react'
import { gapAnalysisFiltersActions as filtersActions } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { type DropdownOption, MultiSelectDropdown } from '../../../UI/multi-select-dropdown/MultiSelectDropdown'
import './FilterOptions.scss'
import {
  type CompetenciesApiArg,
  type ManagersApiArg,
  type ResponseDtoCompetency,
  type ResponseDtoUser,
  useCompetenciesQuery,
  useManagersQuery
} from '../../../../API/rtk-query-api/store/tmsApi'
import Checkbox, { type CheckboxChangeEvent } from '../../../UI/checkbox/Checkbox'

const FilterOptionsGA = () => {
  const dispatch = useAppDispatch()
  const gapAnalysisFiltersState = useAppSelector(state => state.gapAnalysisFilters)

  const [competencySettings, setCompetencySettings] = useState<CompetenciesApiArg>({ page: 1 })
  const [competencyData, setCompetencyData] = useState<ResponseDtoCompetency>()

  const [managerSettings, setManagerSettings] = useState<ManagersApiArg>({ page: 1 })
  const [managerData, setManagerData] = useState<ResponseDtoUser>()

  const setDataAsSelected = useRef(false)

  const { data: competencies, isFetching: isFetchingCompetencies } = useCompetenciesQuery(competencySettings)
  const { data: managers, isFetching: isFetchingManagers } = useManagersQuery(managerSettings)

  useEffect(() => {
    if (competencies && competencies.data && !isFetchingCompetencies) {
      if ((competencies?.page ?? 0) > 1) {
        setCompetencyData({
          ...competencyData,
          data: [...(competencyData?.data ?? []), ...(competencies.data ?? [])]
        })
      } else {
        setCompetencyData(competencies)
      }
    }
  }, [competencies, isFetchingCompetencies])

  useEffect(() => {
    if (setDataAsSelected.current) {
      dispatch(
        filtersActions.setSelectedCompetences(
          competencyData?.data?.map(competency => {
            return { value: competency.id ?? '', text: competency.name ?? '' }
          }) ?? []
        )
      )

      setDataAsSelected.current = false
    }
  }, [competencyData])

  useEffect(() => {
    if (managers && managers.data && !isFetchingManagers) {
      if ((managers.page ?? 0) > 1) {
        setManagerData({
          ...managerData,
          data: [...(managerData?.data ?? []), ...(managers.data ?? [])]
        })
      } else {
        setManagerData(managers)
      }
    }
  }, [managers, isFetchingManagers])

  useEffect(() => {
    if (setDataAsSelected.current) {
      dispatch(
        filtersActions.setSelectedManagers(
          managerData?.data?.map(manager => {
            return { value: manager.id ?? '', text: manager.fullName ?? '' }
          }) ?? []
        )
      )

      setDataAsSelected.current = false
    }
  }, [managerData])

  const competencyDropdownOptions = useMemo(
    () =>
      competencyData?.data?.map(c => {
        return { value: c.id ?? '', text: c.name ?? '' }
      }) ?? [],
    [competencyData?.data]
  )

  const managerDropdownOptions = useMemo(
    () =>
      managerData?.data?.map(m => {
        return { value: m.id ?? '', text: m.fullName ?? '' }
      }) ?? [],
    [managerData?.data]
  )

  // Competencies
  const handleSelectCompetency = (selection: Array<DropdownOption>) => {
    dispatch(filtersActions.setSelectedCompetences(selection))
  }

  const handleSelectAllCompetencies = () => {
    if (competencies?.maxPages ?? 1 > 1) {
      setCompetencySettings({ ...competencySettings, page: undefined })
      setDataAsSelected.current = true
    } else {
      dispatch(filtersActions.setSelectedCompetences(competencyDropdownOptions))
    }
  }

  const handleReachCompetenciesBottom = () => {
    if ((competencySettings.page ?? 0) < (competencies?.maxPages ?? 0)) {
      setCompetencySettings({ ...competencySettings, page: (competencySettings.page ?? 0) + 1 })
    }
  }

  const handleCompetenciesFilter = (filter: string) => {
    setCompetencySettings({ filter: filter, page: 1 })
  }

  // Managers
  const handleSelectManager = (selection: Array<DropdownOption>) => {
    dispatch(filtersActions.setSelectedManagers(selection))
  }

  const handleSelectAllManagers = () => {
    if (managers?.maxPages ?? 0 > 1) {
      setManagerSettings({ ...managerData, page: undefined })
      setDataAsSelected.current = true
    } else {
      dispatch(filtersActions.setSelectedManagers(managerDropdownOptions))
    }
  }

  const handleReachManagersBottom = () => {
    if ((managerSettings.page ?? 0) < (managers?.maxPages ?? 0)) {
      setManagerSettings({ ...managerSettings, page: (managerSettings.page ?? 0) + 1 })
    }
  }

  const handleManagersFilter = (filter: string) => {
    setManagerSettings({ filter: filter, page: 1 })
  }

  // Show-all checkbox handlers
  const handleShowAllChange = (event?: CheckboxChangeEvent) => {
    dispatch(filtersActions.setShowAllReports(event?.checked ?? false))
  }

  return (
    <div className='filter-options'>
      <MultiSelectDropdown
        options={competencyDropdownOptions}
        values={gapAnalysisFiltersState.selectedCompetencies}
        onChange={handleSelectCompetency}
        onLazyLoad={handleReachCompetenciesBottom}
        onFilter={handleCompetenciesFilter}
        onSelectAll={handleSelectAllCompetencies}
        label='Competency'
        placeholder='Select competency'
        totalOptions={competencyData?.total}
        appendTo='self'
        fullWidth
      />
      <MultiSelectDropdown
        options={managerDropdownOptions}
        values={gapAnalysisFiltersState.selectedManagers}
        onChange={handleSelectManager}
        onLazyLoad={handleReachManagersBottom}
        onFilter={handleManagersFilter}
        onSelectAll={handleSelectAllManagers}
        label='Manager'
        placeholder='Select manager'
        totalOptions={managerData?.total}
        appendTo='self'
        fullWidth
      />
      <Checkbox
        name='showAll'
        onChange={handleShowAllChange}
        checked={gapAnalysisFiltersState.showAllReports}
        label='Show All Reports'
      />
    </div>
  )
}

export default FilterOptionsGA
